/**
 * Settings > Agents — UC cache in D1; table view with search, sort, campaign names from catalog.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Tooltip } from '../../components/Tooltip';
import {
  parseAgentDirectoryPayload,
  type AgentDirectoryPayload,
} from '../../lib/agentDirectory';
import {
  buildCampaignIdToTitleMap,
  resolveCampaignCodeToName,
} from '../../lib/campaignCodeToName';

type AgentRow = {
  agent_id: number;
  payload: string;
  synced_at: string | null;
};

type CatalogRow = {
  campaign_id: number;
  campaign_title: string | null;
};

type TableSort = 'name-az' | 'name-za' | 'state-az' | 'state-za' | 'team-az' | 'team-za';

type ParsedRow = {
  agent_id: number;
  name: string;
  email: string;
  team: string;
  state: string;
  pause: string;
  synced_at: string | null;
  campaignCodes: string[];
  campaignResolvedNames: string[];
  campaignsDisplay: string;
};

function parsePayload(r: AgentRow): AgentDirectoryPayload | null {
  try {
    return JSON.parse(r.payload) as AgentDirectoryPayload;
  } catch {
    return null;
  }
}

function formatSyncedAtLocal(v: string | null): string {
  if (!v) return '—';
  const normalized = v.includes('T') ? v : `${v.replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString();
}

export default function SettingsAgents() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rows, setRows] = useState<AgentRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [presenceWsUrl, setPresenceWsUrl] = useState('');
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [sortBy, setSortBy] = useState<TableSort>('name-az');

  const workerOrigin = useMemo(() => {
    const ws = presenceWsUrl.trim();
    if (!ws.startsWith('ws')) return '';
    try {
      const normalized = ws.startsWith('wss://')
        ? `https://${ws.slice('wss://'.length)}`
        : `http://${ws.slice('ws://'.length)}`;
      return new URL(normalized).origin;
    } catch {
      return '';
    }
  }, [presenceWsUrl]);

  const idToTitle = useMemo(() => buildCampaignIdToTitleMap(catalog), [catalog]);

  const loadRows = useCallback(async () => {
    if (!workerOrigin) throw new Error('Set presence_ws_url in Integrations first.');
    const res = await fetch(`${workerOrigin}/agents/catalog`);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error((d as { error?: string }).error || `HTTP ${res.status}`);
    }
    const data = (await res.json()) as { agents?: AgentRow[] };
    setRows(Array.isArray(data.agents) ? data.agents : []);
  }, [workerOrigin]);

  const loadCatalog = useCallback(async () => {
    if (!workerOrigin) return;
    const res = await fetch(`${workerOrigin}/campaigns/catalog`);
    if (!res.ok) return;
    const data = (await res.json()) as { campaigns?: CatalogRow[] };
    setCatalog(Array.isArray(data.campaigns) ? data.campaigns : []);
  }, [workerOrigin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const cfgRes = await fetch(`${base}/api/config?keys=presence_ws_url`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          if (typeof cfg.presence_ws_url === 'string' && !cancelled) setPresenceWsUrl(cfg.presence_ws_url);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        await Promise.all([loadRows(), loadCatalog()]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load agents');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin, loadRows, loadCatalog]);

  const parsedRows = useMemo((): ParsedRow[] => {
    const out: ParsedRow[] = [];
    for (const r of rows) {
      const p = parsePayload(r);
      const uc = p?.uc;
      const rawCodes = Array.isArray(uc?.campaigns)
        ? (uc!.campaigns as unknown[]).filter((x): x is string => typeof x === 'string')
        : [];
      const campaignResolvedNames: string[] = [];
      const seen = new Set<string>();
      for (const c of rawCodes) {
        const nm = resolveCampaignCodeToName(c, idToTitle);
        if (seen.has(nm)) continue;
        seen.add(nm);
        campaignResolvedNames.push(nm);
      }
      const display = parseAgentDirectoryPayload(r.agent_id, r.payload);
      const name = display?.name ?? `Agent ${r.agent_id}`;
      out.push({
        agent_id: r.agent_id,
        name,
        email: typeof uc?.email === 'string' ? uc.email : '',
        team: p?.resolvedTeam ?? '',
        state: typeof uc?.state === 'string' ? uc.state : '—',
        pause:
          uc?.pause_reason != null && String(uc.pause_reason).trim() !== ''
            ? String(uc.pause_reason)
            : '—',
        synced_at: r.synced_at,
        campaignCodes: rawCodes,
        campaignResolvedNames,
        campaignsDisplay: campaignResolvedNames.length ? campaignResolvedNames.join(', ') : '—',
      });
    }
    return out;
  }, [rows, idToTitle]);

  const campaignOptions = useMemo(() => {
    const s = new Set<string>();
    for (const p of parsedRows) {
      for (const n of p.campaignResolvedNames) s.add(n);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [parsedRows]);

  const filteredSorted = useMemo(() => {
    let list = parsedRows;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.team.toLowerCase().includes(q) ||
          r.state.toLowerCase().includes(q) ||
          r.campaignsDisplay.toLowerCase().includes(q)
      );
    }
    if (campaignFilter) {
      list = list.filter((r) => r.campaignResolvedNames.includes(campaignFilter));
    }
    const sorted = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name-az':
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        case 'name-za':
          return b.name.localeCompare(a.name, undefined, { sensitivity: 'base' });
        case 'state-az':
          return a.state.localeCompare(b.state, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        case 'state-za':
          return b.state.localeCompare(a.state, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        case 'team-az':
          return a.team.localeCompare(b.team, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        case 'team-za':
          return b.team.localeCompare(a.team, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    return sorted;
  }, [parsedRows, search, campaignFilter, sortBy]);

  const handleRefresh = async () => {
    if (!workerOrigin) {
      setError('Set presence_ws_url in Integrations first.');
      return;
    }
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${workerOrigin}/agents/refresh`, { method: 'POST' });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        upserted?: number;
        reason?: string;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(data.reason || data.error || `HTTP ${res.status}`);
      }
      if (data.ok === false) {
        throw new Error(data.reason || data.error || 'Refresh failed');
      }
      const upserted = data.upserted ?? 0;
      setSuccess(`Updated ${upserted} agent(s).`);
      await Promise.all([loadRows(), loadCatalog()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  }

  const thStyle: Record<string, string | number> = {
    padding: '0.45rem 0.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
      <h2 style={{ margin: 0, fontSize: '1.125rem' }}>Agents</h2>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <Tooltip content="UC GET /agent-state on the Presence Worker. Updates D1 cache.">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={syncing}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: syncing ? 'not-allowed' : 'pointer',
            }}
          >
            {syncing ? 'Refreshing…' : 'Refresh list'}
          </button>
        </Tooltip>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          {parsedRows.length} cached · {filteredSorted.length} shown
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search name, email, team, status, campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 200px',
            minWidth: 160,
            padding: '0.45rem 0.65rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '0.8125rem',
          }}
        />
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          style={{
            padding: '0.45rem 0.65rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '0.8125rem',
            minWidth: 180,
          }}
        >
          <option value="">All campaigns</option>
          {campaignOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as TableSort)}
          style={{
            padding: '0.45rem 0.65rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '0.8125rem',
          }}
        >
          <option value="name-az">Name A–Z</option>
          <option value="name-za">Name Z–A</option>
          <option value="state-az">Status A–Z</option>
          <option value="state-za">Status Z–A</option>
          <option value="team-az">Team A–Z</option>
          <option value="team-za">Team Z–A</option>
        </select>
      </div>

      {success && <p style={{ color: '#22c55e', fontSize: '0.875rem', margin: 0 }}>{success}</p>}
      {error && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: 0 }}>{error}</p>}

      <div
        style={{
          width: '100%',
          minHeight: 'calc(100vh - 13.5rem)',
          maxHeight: 'calc(100vh - 13.5rem)',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        {filteredSorted.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '1rem' }}>
            {parsedRows.length === 0 ? 'No cached agents. Use Refresh list.' : 'No rows match filters.'}
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--widget-bg)' }}>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Team</th>
                <th style={{ ...thStyle, minWidth: 220 }}>Campaigns</th>
                <th style={thStyle}>Pause</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Synced</th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((r) => (
                <tr key={r.agent_id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)' }}>{r.agent_id}</td>
                  <td style={{ padding: '0.4rem 0.5rem', fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{r.state}</td>
                  <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)' }}>{r.team || '—'}</td>
                  <td style={{ padding: '0.4rem 0.5rem', maxWidth: 360, wordBreak: 'break-word' }}>{r.campaignsDisplay}</td>
                  <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)', maxWidth: 140 }}>{r.pause}</td>
                  <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)', maxWidth: 200 }}>{r.email || '—'}</td>
                  <td style={{ padding: '0.4rem 0.5rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {formatSyncedAtLocal(r.synced_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
